import { api } from './api';

export type RecommendedAction = {
  action_id: string;
  label: string;
  mcp_tool_name?: string;
  default_inputs: Record<string, any>;
  permission_required: string;
  why: string[];
  expected_duration: number;
  post_action_navigation?: string;
};

export type EdgeHealthResponse = {
  ok?: boolean;
  error?: string;
  detail?: string;
  /**
   * Edge health status. Historically: healthy|degraded, but edges/cloud proxy may return other strings
   * (or omit it on error payloads).
   */
  status?: string;
  reason_codes?: string[];
  reason_codes_structured?: Array<{
    code: string;
    severity: 'high' | 'medium' | 'low' | string;
    resource_type: string;
    resource_id?: string;
    details?: Record<string, any>;
  }>;
  reason_detail?: Record<string, any>;
  recommended_actions?: RecommendedAction[];
  runbook_refs?: string[];
  recommended_actions_version?: string;
  templates_version?: string;
  device_id?: string;
  ip_address?: string;
  timestamp?: string;
};

type EdgeHealthFetchOptions = {
  edgeHost?: string;
  force?: boolean;
  ttlMs?: number;
};

const EDGE_HEALTH_TTL_MS = 15_000;
const edgeHealthCache = new Map<string, { ts: number; data: EdgeHealthResponse | null }>();
const edgeHealthInflight = new Map<string, Promise<EdgeHealthResponse | null>>();

function edgeHealthCacheKey(deviceId: string, edgeHost?: string): string {
  return `${String(deviceId || '').trim()}::${String(edgeHost || '').trim()}`;
}

export async function fetchEdgeHealth(deviceId: string, opts?: EdgeHealthFetchOptions): Promise<EdgeHealthResponse | null> {
  if (!deviceId) return null;
  const edgeHost = String(opts?.edgeHost || '').trim();
  const ttlMs = Number.isFinite(opts?.ttlMs as number) ? Number(opts?.ttlMs) : EDGE_HEALTH_TTL_MS;
  const key = edgeHealthCacheKey(deviceId, edgeHost);
  const now = Date.now();

  if (!opts?.force) {
    const cached = edgeHealthCache.get(key);
    if (cached && now - cached.ts <= ttlMs) {
      return cached.data;
    }
    const inflight = edgeHealthInflight.get(key);
    if (inflight) {
      return inflight;
    }
  }

  const promise = api
    .get(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/health`, {
      ...(edgeHost ? { params: { edge_host: edgeHost } } : {}),
    })
    .then((res) => {
      const data = (res?.data ?? null) as EdgeHealthResponse | null;
      edgeHealthCache.set(key, { ts: Date.now(), data });
      return data;
    })
    .finally(() => {
      edgeHealthInflight.delete(key);
    });

  edgeHealthInflight.set(key, promise);
  return promise;
}

/**
 * Runbook template IDs are server-defined (data-driven).
 * Keep this type wide so newly-added server templates don't require a frontend rebuild.
 */
export type OpsRunbookId = string;

export type OpsRunbookKind = 'guided' | 'execute';

export type OpsRunbookStepResult = {
  ok: boolean;
  status: 'pending' | 'running' | 'succeeded' | 'failed';
  started_at?: string;
  finished_at?: string;
  error?: string;
  data?: any;
};

export type OpsRunbookHttpRequest = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /**
   * Absolute API path (recommended), e.g. `/api/v1/pipeline/devices/{{device_id}}/...`
   * Placeholders supported: `{{device_id}}`
   */
  path: string;
  /** JSON body for non-GET requests (placeholders supported in nested strings). */
  body?: any;
};

export type OpsRunbookStep = {
  id: string;
  title: string;
  detail?: string;
  request?: OpsRunbookHttpRequest;
};

export type OpsRunbookTemplate = {
  id: OpsRunbookId;
  title: string;
  summary: string;
  estimated_sec?: number;
  kind: OpsRunbookKind;
  requires_device?: boolean;
  /**
   * For guided runbooks, the frontend navigates to this URL.
   * Placeholders supported: `{{device_id}}` and any query-scoped context key
   * passed to `resolveGuidedRunbookHref()` (for example `zone_id`, `incident_type`,
   * `from_date`, or `to_date`).
   */
  href_template?: string;
  steps?: OpsRunbookStep[];
};

export type OpsRunbookExecution = {
  execution_id: string;
  runbook_id: OpsRunbookId;
  runbook_title: string;
  kind: OpsRunbookKind;
  device_id?: string;
  site_id?: string;
  actor?: string;
  started_at: string;
  finished_at?: string;
  status: 'running' | 'succeeded' | 'failed' | 'blocked' | 'cancelled' | string;
  params?: Record<string, any>;
  incident_id?: string;
  case_id?: string;
  proof_package_id?: string;
  proof_surface?: string;
  source?: string;
  zone_id?: string;
  incident_type?: string;
  from_date?: string;
  to_date?: string;
  steps?: Array<{
    id: string;
    title: string;
    detail?: string;
    result: OpsRunbookStepResult;
  }>;
};

const STORAGE_KEY = 'edgentik:ops_copilot_executions:v1';

function _safeJsonParse(raw: string): any | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function listOpsCopilotExecutions(): OpsRunbookExecution[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const v = _safeJsonParse(raw);
    if (!Array.isArray(v)) return [];
    return v as OpsRunbookExecution[];
  } catch {
    return [];
  }
}

export function saveOpsCopilotExecution(ex: OpsRunbookExecution): void {
  try {
    const all = listOpsCopilotExecutions();
    const next = [ex, ...all.filter((x) => x?.execution_id !== ex.execution_id)].slice(0, 200);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore (storage disabled)
  }
}

function getOpsRunbookTemplatesLocalFallback(): OpsRunbookTemplate[] {
  return [
    {
      id: 'bringup_gate',
      title: 'Bring-up gate (commissioning)',
      summary: 'Single flow: onboard + setup + desired-state sync + strict preflight gate + commissioning artifacts.',
      estimated_sec: 3600,
      kind: 'guided',
      requires_device: false,
      href_template: '/bringup?device={{device_id}}',
    },
    {
      id: 'safety_area_hardening',
      title: 'Safety area corrective action workflow',
      summary: 'Guided hotspot workflow: review incidents, confirm controls, assign follow-through, and schedule the next review.',
      estimated_sec: 900,
      kind: 'guided',
      requires_device: true,
      href_template: '/incidents?device_id={{device_id}}&zone_id={{zone_id}}&incident_type={{incident_type}}&from_date={{from_date}}&to_date={{to_date}}&tab=open&table_mode=grouped&focus={{focus}}&workflow={{workflow}}',
      steps: [
        {
          id: 'review_hotspot_incidents',
          title: 'Review the hotspot incident set',
          detail: 'Open the incident queue for this device and confirm the repeating area-control pattern.',
        },
        {
          id: 'confirm_controls',
          title: 'Confirm current area controls',
          detail: 'Check signage, barriers, boundaries, and worker movement rules against what the incidents show.',
        },
        {
          id: 'assign_owner',
          title: 'Assign owner and next action',
          detail: 'Set who owns the corrective action and what change will be made first.',
        },
        {
          id: 'schedule_followup',
          title: 'Schedule follow-up review',
          detail: 'Set the next review time so the team can verify whether the corrective action reduced the pattern.',
        },
        {
          id: 'return_to_proof',
          title: 'Return to proof and compare the next window',
          detail: 'Refresh the proof workspace after changes so improvement is measured against the current baseline.',
        },
      ],
    },
    {
      id: 'convergence_recover',
      title: 'Convergence recovery (config)',
      summary: 'Re-sync config (policies/zones) when applied drifts. Config-only; does not update release or firmware.',
      estimated_sec: 90,
      kind: 'execute',
      requires_device: true,
      steps: [
        {
          id: 'proof_before',
          title: 'Fetch desired/applied proof',
          detail: 'GET desired_state/status',
          request: {
            method: 'GET',
            path: '/api/v1/pipeline/devices/{{device_id}}/edge/desired_state/status',
          },
        },
        {
          id: 'sync',
          title: 'Sync desired state to edge',
          detail: 'POST desired_state/sync',
          request: {
            method: 'POST',
            path: '/api/v1/pipeline/devices/{{device_id}}/edge/desired_state/sync',
            body: {},
          },
        },
        {
          id: 'proof_after',
          title: 'Re-check desired/applied proof',
          detail: 'GET desired_state/status (again)',
          request: {
            method: 'GET',
            path: '/api/v1/pipeline/devices/{{device_id}}/edge/desired_state/status',
          },
        },
      ],
    },
    {
      id: 'device_update_complete',
      title: 'Complete update (OTA)',
      summary: 'Preflight → set desired release → native update → release agent poll. For release/firmware drift.',
      estimated_sec: 300,
      kind: 'execute',
      requires_device: true,
      steps: [
        { id: 'preflight', title: 'OTA precheck', detail: 'POST release/precheck', request: { method: 'POST', path: '/api/v1/pipeline/devices/{{device_id}}/edge/release/precheck', body: { desired_release_id: '{{desired_release_id}}' } } },
        { id: 'set_release', title: 'Set desired release', detail: 'PUT desired_release', request: { method: 'PUT', path: '/api/v1/devices/{{device_id}}/desired_release', body: { desired_release_id: '{{desired_release_id}}' } } },
        { id: 'apply_update', title: 'Trigger native update', detail: 'POST update/edgentik', request: { method: 'POST', path: '/api/v1/pipeline/devices/{{device_id}}/edge/update/edgentik', body: {} } },
        { id: 'release_agent_poll', title: 'Trigger release agent poll', detail: 'POST release/agent/poll_now', request: { method: 'POST', path: '/api/v1/pipeline/devices/{{device_id}}/edge/release/agent/poll_now', body: { reason: 'device_update_complete' } } },
      ],
    },
    {
      id: 'incident_evidence_recover',
      title: 'Incident evidence recovery',
      summary: 'Recover missing incident proof by checking device evidence health, resetting failed uploads, and retrying ticketing sync.',
      estimated_sec: 120,
      kind: 'execute',
      requires_device: true,
      steps: [
        {
          id: 'device_incident_health',
          title: 'Check device incident/evidence health',
          detail: 'GET device health/incident_e2e',
          request: { method: 'GET', path: '/api/v1/pipeline/devices/{{device_id}}/health/incident_e2e' },
        },
        {
          id: 'outbox_summary_before',
          title: 'Inspect evidence outbox summary',
          detail: 'GET edge/outbox/summary',
          request: { method: 'GET', path: '/api/v1/pipeline/devices/{{device_id}}/edge/outbox/summary' },
        },
        {
          id: 'reset_failed_evidence_uploads',
          title: 'Reset failed evidence uploads',
          detail: 'POST edge/outbox/reset_failed',
          request: {
            method: 'POST',
            path: '/api/v1/pipeline/devices/{{device_id}}/edge/outbox/reset_failed',
            body: {
              job_type: 'EVIDENCE_UPLOAD',
              limit: 200,
              reset_attempts: true,
              confirm: 'reset_failed',
            },
          },
        },
        {
          id: 'ticketing_retry',
          title: 'Retry evidence/ticket delivery',
          detail: 'POST /api/v1/pipeline/ticketing/retry',
          request: { method: 'POST', path: '/api/v1/pipeline/ticketing/retry', body: { limit: 50 } },
        },
        {
          id: 'outbox_summary_after',
          title: 'Verify evidence outbox after recovery',
          detail: 'GET edge/outbox/summary',
          request: { method: 'GET', path: '/api/v1/pipeline/devices/{{device_id}}/edge/outbox/summary' },
        },
      ],
    },
    {
      id: 'evidence_upload_recover',
      title: 'Evidence upload / ticketing recovery',
      summary: 'Check incident pipeline health and kick the ticketing retry queue (after Odoo/evidence outages).',
      estimated_sec: 60,
      kind: 'execute',
      requires_device: false,
      steps: [
        {
          id: 'incident_health',
          title: 'Check incident pipeline health',
          detail: 'GET /api/v1/health/incident_e2e',
          request: { method: 'GET', path: '/api/v1/health/incident_e2e' },
        },
        {
          id: 'ticketing_retry',
          title: 'Kick ticketing retry queue',
          detail: 'POST /api/v1/pipeline/ticketing/retry',
          request:         { method: 'POST', path: '/api/v1/pipeline/ticketing/retry', body: { limit: 50 } },
        },
      ],
    },
    {
      id: 'outbox_clear_pending',
      title: 'Clear pending evidence backlog (drop)',
      summary: 'Permanently drop PENDING outbox jobs to clear backlog (e.g. after long cloud outage). Use when evidence files are gone or operator accepts data loss.',
      estimated_sec: 60,
      kind: 'execute',
      requires_device: true,
      steps: [
        {
          id: 'clear',
          title: 'Clear PENDING outbox jobs',
          detail: 'POST edge/outbox/clear_pending',
          request: {
            method: 'POST',
            path: '/api/v1/pipeline/devices/{{device_id}}/edge/outbox/clear_pending',
            body: {
              job_type: 'EVIDENCE_UPLOAD',
              limit: 15000,
              confirm: 'clear_pending',
            },
          },
        },
      ],
    },
    {
      id: 'camera_ingest_down',
      title: 'Camera ingest down triage',
      summary: 'Run strict preflight (camera checks) and restart orchestrator (safe remediation baseline).',
      estimated_sec: 180,
      kind: 'execute',
      requires_device: true,
      steps: [
        {
          id: 'preflight',
          title: 'Run strict preflight (cameras)',
          detail: 'POST edge/preflight/run',
          request: {
            method: 'POST',
            path: '/api/v1/pipeline/devices/{{device_id}}/edge/preflight/run',
            body: { cameras: true },
          },
        },
        {
          id: 'orchestrator_restart',
          title: 'Restart orchestrator',
          detail: 'POST edge/orchestrator/restart',
          request: {
            method: 'POST',
            path: '/api/v1/pipeline/devices/{{device_id}}/edge/orchestrator/restart',
            body: {},
          },
        },
      ],
    },
    {
      id: 'rollback_lkg',
      title: 'Rollback to last-known-good (LKG)',
      summary: 'Perform a rollback dry-run (default). Optionally execute the rollback if approved.',
      estimated_sec: 240,
      kind: 'execute',
      requires_device: true,
      steps: [
        {
          id: 'rollback_dry_run',
          title: 'Rollback dry-run (no changes)',
          detail: 'POST edge/rollback prefer=lkg dry_run=true',
          request: {
            method: 'POST',
            path: '/api/v1/pipeline/devices/{{device_id}}/edge/rollback',
            body: { prefer: 'lkg', dry_run: true },
          },
        },
      ],
    },
  ];
}

export async function fetchOpsRunbookTemplates(): Promise<OpsRunbookTemplate[]> {
  try {
    const res = await api.get('/api/v1/ops/runbooks/templates');
    const items = Array.isArray(res.data?.templates) ? (res.data.templates as OpsRunbookTemplate[]) : [];
    return items;
  } catch {
    // Fallback for older servers / local dev.
    return getOpsRunbookTemplatesLocalFallback();
  }
}

export function resolveGuidedRunbookHref(
  template: OpsRunbookTemplate,
  ctx: Record<string, string | undefined>,
): string | null {
  if (!template.href_template) return null;
  let resolved = String(template.href_template || '');
  const placeholderMatches = Array.from(resolved.matchAll(/\{\{([^}]+)\}\}/g));
  const placeholderKeys = Array.from(new Set(placeholderMatches.map((match) => String(match[1] || '').trim()).filter(Boolean)));

  for (const key of placeholderKeys) {
    const value = String((ctx as any)?.[key] || '').trim();
    resolved = resolved.split(`{{${key}}}`).join(value ? encodeURIComponent(value) : '');
  }

  const [pathPart, queryPart] = resolved.split('?');
  const cleanPath = pathPart || '';
  if (!queryPart) return cleanPath || null;

  const params = new URLSearchParams(queryPart);
  const toDelete: string[] = [];
  params.forEach((value, key) => {
    if (!String(value || '').trim()) toDelete.push(key);
  });
  for (const key of toDelete) params.delete(key);
  const query = params.toString();
  return cleanPath + (query ? `?${query}` : '');
}

export async function listOpsRunbookExecutionsServer(limit: number = 50): Promise<OpsRunbookExecution[]> {
  const res = await api.get('/api/v1/ops/runbooks/executions', { params: { limit } });
  return Array.isArray(res.data?.executions) ? (res.data.executions as OpsRunbookExecution[]) : [];
}

export async function getOpsRunbookExecutionServer(execution_id: string): Promise<any> {
  const res = await api.get(`/api/v1/ops/runbooks/executions/${encodeURIComponent(execution_id)}`);
  return res.data;
}

export function normalizeOpsRunbookExecution(
  exec: any,
  fallback: Partial<OpsRunbookExecution> = {},
): OpsRunbookExecution {
  const steps: OpsRunbookExecution['steps'] = Array.isArray(exec?.steps)
    ? exec.steps.map((s: any) => ({
        id: s.step_id || s.id || 'step',
        title: s.title || '',
        detail: s.detail,
        result: {
          ok: Boolean(s.ok),
          status:
            (s.status as OpsRunbookStepResult['status'])
            || (s.ok ? 'succeeded' : 'failed'),
          started_at: s.started_at,
          finished_at: s.finished_at,
          error: s.error,
          data: s.data,
        },
      }))
    : fallback.steps;

  return {
    execution_id: exec?.execution_id || fallback.execution_id || '',
    runbook_id: exec?.runbook_id || fallback.runbook_id || '',
    runbook_title: exec?.runbook_title || fallback.runbook_title || '',
    kind: (exec?.kind || fallback.kind || 'execute') as OpsRunbookKind,
    device_id: exec?.device_id || fallback.device_id,
    site_id: exec?.site_id || fallback.site_id,
    actor: exec?.actor || fallback.actor,
    started_at: exec?.started_at || fallback.started_at || '',
    finished_at: exec?.finished_at || fallback.finished_at,
    status: exec?.status || fallback.status || 'failed',
    params: exec?.params || fallback.params,
    incident_id: exec?.incident_id || fallback.incident_id,
    case_id: exec?.case_id || fallback.case_id,
    proof_package_id: exec?.proof_package_id || fallback.proof_package_id,
    proof_surface: exec?.proof_surface || fallback.proof_surface,
    source: exec?.source || fallback.source,
    zone_id: exec?.zone_id || fallback.zone_id,
    incident_type: exec?.incident_type || fallback.incident_type,
    from_date: exec?.from_date || fallback.from_date,
    to_date: exec?.to_date || fallback.to_date,
    steps,
  };
}

/**
 * Execute runbook via server-side endpoint (Phase 1 canonical path).
 * Use this for execute-kind runbooks. Guided runbooks still return early.
 */
export async function executeRunbookServer(
  template: OpsRunbookTemplate,
  ctx: { device_id?: string; actor?: string; params?: Record<string, any> } = {},
): Promise<OpsRunbookExecution> {
  const body: Record<string, any> = {
    device_id: ctx.device_id || undefined,
    site_id: (ctx.params as any)?.site_id || undefined,
    org_id: (ctx.params as any)?.org_id || undefined,
    params: ctx.params || {},
  };

  const res = await api.post(
    `/api/v1/ops/runbooks/templates/${encodeURIComponent(template.id)}/execute`,
    body,
  );
  const data = res?.data;
  const exec = data?.execution;
  if (!exec) {
    throw new Error(data?.error || 'No execution in response');
  }
  return normalizeOpsRunbookExecution(exec, {
    execution_id: exec.execution_id,
    runbook_id: exec.runbook_id || template.id,
    runbook_title: exec.runbook_title || template.title,
    kind: template.kind,
    device_id: exec.device_id || ctx.device_id,
    actor: exec.actor || ctx.actor,
    started_at: exec.started_at,
    finished_at: exec.finished_at,
    status: exec.status || (data?.ok ? 'succeeded' : 'failed'),
    params: ctx.params,
    site_id: (ctx.params as any)?.site_id || undefined,
    incident_id: (ctx.params as any)?.incident_id || undefined,
    case_id: (ctx.params as any)?.case_id || (ctx.params as any)?.incident_id || undefined,
    proof_package_id: (ctx.params as any)?.proof_package_id || undefined,
    proof_surface: (ctx.params as any)?.proof_surface || undefined,
    source: (ctx.params as any)?.source || undefined,
    zone_id: (ctx.params as any)?.zone_id || undefined,
    incident_type: (ctx.params as any)?.incident_type || undefined,
    from_date: (ctx.params as any)?.from_date || undefined,
    to_date: (ctx.params as any)?.to_date || undefined,
  });
}

/**
 * Run runbook via server-side execute (Phase 1 canonical path).
 * All runbooks (guided + execute) flow through POST .../templates/{id}/execute.
 */
export async function runOpsRunbook(
  template: OpsRunbookTemplate,
  ctx: { device_id?: string; actor?: string; params?: Record<string, any> } = {},
): Promise<OpsRunbookExecution> {
  const ex = await executeRunbookServer(template, ctx);
  saveOpsCopilotExecution(ex);
  return ex;
}
